import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import "./HeroSection.css";
import heroLocalImg from "../images/801A0068.jpg";
import doctorFamilyImg from "../images/801A0256.jpg";
import hospitalWorkImg from "../images/Gundeti_Foundation_Hospital_work.jpeg";

const slides = [
  {
    img: heroLocalImg,
    heading: "Gundeti Foundation",
    subheading: "CHANGING LIVES",
    detail: "Pediatric Urological Surgical Care",
    highlight: "Hope & Healing",
    tagline: "Dedicated to children in need.",
    button: "Learn More",
  },
  {
    img: doctorFamilyImg,
    heading: "Care & Compassion",
    subheading: "PATIENT FOCUS",
    detail: "Serving Families with Dedication",
    highlight: "Every Child Matters",
    tagline: "Together, we bring hope and healing.",
    button: "Our Mission",
  },
  {
    img: hospitalWorkImg,
    heading: "Hospital Work",
    subheading: "DEDICATED SERVICE",
    detail: "Transforming Pediatric Healthcare",
    highlight: "Expert Team",
    tagline: "Delivering quality care and support.",
    button: "See Our Work",
  },
];

const textVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const AUTOPLAY_INTERVAL = 5000;

const HeroSection = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef();

  const goToSlide = (i) => {
    setIndex(i);
    setPaused(true);
  };

  useEffect(() => {
    if (!paused) {
      timerRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % slides.length);
      }, AUTOPLAY_INTERVAL);
    }
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  useEffect(() => {
    if (paused) {
      const resume = setTimeout(() => setPaused(false), AUTOPLAY_INTERVAL);
      return () => clearTimeout(resume);
    }
  }, [paused]);

  return (
    <Box
      className="hero"
      sx={{
        fontFamily: "Poppins, Arial, sans-serif",
        background: "none",
        position: "relative",
        minHeight: { xs: 500, md: 800 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 0, md: 0 },
        boxSizing: "border-box",
        zIndex: 1,
      }}
      component={motion.div}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <Box
        className="hero-inner"
        sx={{
          width: { xs: "100%", md: "100%" },
          minHeight: { xs: 500, md: 800 },
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, var(--color-light-blue), var(--color-light-green))",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
            }}
          >
            <Box
              className="hero-bg"
              sx={{
                width: "100%",
                height: "100%",
                backgroundImage: `url(${slides[index].img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                position: "absolute",
                inset: 0,
                zIndex: 1,
                "&:after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)",
                  zIndex: 2,
                },
              }}
            />
          </motion.div>
        </AnimatePresence>
        <Container sx={{ position: "relative", zIndex: 3 }}>
          <motion.div
            key={index + "-text"}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={textVariant}
          >
            <Box
              className="hero-content"
              sx={{
                minHeight: { xs: 500, md: 800 },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
                justifyContent: "center",
                color: "var(--color-black)",
                textAlign: { xs: "center", md: "left" },
                position: "relative",
                zIndex: 5,
                px: { xs: 2, md: 8 },
              }}
            >
              <Typography
                className="hero-title"
                variant="h5"
                sx={{
                  color: "var(--color-accent-green)",
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                {slides[index].heading}
              </Typography>
              <Typography
                className="hero-subtitle"
                variant="h4"
                sx={{
                  fontWeight: 900,
                  mb: 2,
                  color: "var(--color-accent-blue)",
                  lineHeight: 1.1,
                }}
              >
                {slides[index].subheading}
              </Typography>
              <Typography
                className="hero-detail"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "var(--color-accent-blue)",
                  mb: 2,
                }}
              >
                {slides[index].detail}
              </Typography>
              <Typography
                className="hero-highlight"
                variant="h4"
                sx={{
                  color: "var(--color-accent-blue)",
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                {slides[index].highlight}
              </Typography>
              <Typography
                className="hero-tagline"
                variant="h6"
                sx={{ color: "var(--color-black)", mb: 3 }}
              >
                {slides[index].tagline}
              </Typography>
              <Button
                className="hero-cta"
                variant="contained"
                sx={{
                  backgroundColor: "var(--color-accent-blue)",
                  color: "var(--color-white)",
                  borderRadius: 999,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  textTransform: "uppercase",
                  "&:hover": { backgroundColor: "var(--color-accent-green)" },
                }}
              >
                {slides[index].button}
              </Button>
            </Box>
          </motion.div>
        </Container>
        {/* Dots */}
        <Box
          className="hero-dots"
          sx={{
            position: "absolute",
            bottom: 32,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          {slides.map((_, i) => (
            <Box
              key={i}
              onClick={() => goToSlide(i)}
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background:
                  i === index
                    ? "var(--color-accent-blue)"
                    : "rgba(255,255,255,0.7)",
                mx: 1,
                cursor: "pointer",
                border:
                  i === index
                    ? "2px solid var(--color-white)"
                    : "2px solid transparent",
                transition: "background 0.3s, border 0.3s",
                boxShadow:
                  i === index ? "0 0 0 2px var(--color-accent-blue)" : "none",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
